import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { AuthLayout } from '@/components/layout/auth-layout';
import { apiClient, type ApiError } from '@/lib/api-client';
import { useAuthStore } from '@/lib/auth-store';

function extractApiMessage(err: unknown, fallback: string): string {
  const body = (err as { body?: unknown })?.body;
  const raw = (body as { message?: unknown })?.message;
  if (typeof raw === 'string') return raw;
  return (err as Error)?.message ?? fallback;
}

export default function SignUpPage() {
  const navigate = useNavigate();
  const signIn = useAuthStore((s) => s.signIn);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [oab, setOab] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await apiClient.signUp({
        name,
        email,
        password,
        ...(oab.trim() ? { oab: oab.trim() } : {}),
      });
      // Apos cadastro, faz sign-in pra estabelecer cookie + tokens.
      const res = await signIn(email, password);
      if (res.mfaRequired) {
        navigate('/mfa-verify');
        return;
      }
      const state = useAuthStore.getState();
      if (state.isSuperAdmin) {
        navigate('/admin');
      } else if (!state.tenantId) {
        navigate('/sem-escritorio');
      } else {
        navigate('/escritorio');
      }
    } catch (err) {
      const status = (err as ApiError | undefined)?.status;
      if (status === 403) {
        setError(
          'Cadastros públicos foram desabilitados. Entre em contato com o administrador.',
        );
      } else {
        setError(extractApiMessage(err, 'Falha ao cadastrar'));
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout>
      <Card>
        <CardHeader>
          <CardTitle>Criar conta</CardTitle>
          <CardDescription>Comece em segundos. OAB e opcional.</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome completo</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                minLength={2}
                maxLength={120}
                autoComplete="name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Senha</Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={12}
              />
              <p className="text-xs text-[var(--fg-muted)]">
                Pelo menos 12 caracteres, com maiuscula, minuscula e digito.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="oab">OAB (opcional)</Label>
              <Input
                id="oab"
                placeholder="087113/RS"
                value={oab}
                onChange={(e) => setOab(e.target.value)}
              />
            </div>
            {error && (
              <p className="text-sm text-red-600" role="alert">
                {error}
              </p>
            )}
          </CardContent>
          <CardFooter className="flex-col gap-3">
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Criando...' : 'Criar conta'}
            </Button>
            <p className="text-center text-sm text-[var(--fg-muted)]">
              Ja tem conta?{' '}
              <Link to="/sign-in" className="text-[var(--fg)] hover:underline">
                Entrar
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </AuthLayout>
  );
}
